import React from 'react';
import {ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid} from 'recharts';

interface SalesStat {
    label: string;
    value: string;
}

interface SalesChartPoint {
    label: string;
    value: number;
}

interface SalesTooltipProps {
    active?: boolean;
    payload?: Array<{value: unknown}>;
    label?: string;
}

const SalesTooltip: React.FC<SalesTooltipProps> = ({active, payload, label}) => {
    if (active && payload && payload.length) {
        const rawValue = payload[0].value;
        const value = typeof rawValue === 'number' ? rawValue : Number(rawValue);
        const formatted = Number.isFinite(value)
            ? value.toLocaleString('en-US', {style: 'currency', currency: 'USD'})
            : '—';
        return (
            <div className="rounded-xl border border-[#d7e4e1] bg-white px-4 py-3 shadow-sm text-sm text-[#2b4144]">
                <p className="font-semibold">{label}</p>
                <p className="text-[#2d8473]">{formatted}</p>
            </div>
        );
    }
    return null;
};

export interface SalesReportChartProps {
    reportTabs: string[];
    activeRange: string;
    onSelectRange: (range: string) => void;
    salesStats: SalesStat[];
    salesChartData: SalesChartPoint[];
    maxSalesValue: number;
}

const SalesReportChart: React.FC<SalesReportChartProps> = ({
    reportTabs,
    activeRange,
    onSelectRange,
    salesStats,
    salesChartData,
    maxSalesValue,
}) => {
    return (
        <div>
            <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <h2 className="text-[27px] font-medium text-mld-text-primary mb-2 ">Sales Reports</h2>
                <button className="text-mld-muted-text hover:text-[#42D4B0]" type="button">
                    View All
                </button>
            </div>

            <div className="grid gap-6 lg:grid-cols-[250px_1fr_1fr] rounded-2xl border border-mld-panel-border bg-mld-menu-background">
                <div className="grid gap-4 border-r border-mld-panel-border divide-y divide-mld-panel-border">
                    {salesStats.map((stat) => (
                        <div key={stat.label} className="flex flex-col justify-center p-6 h-26">
                            <p className="text-mld-text-primary text-2xl">{stat.value}</p>
                            <p className="text-mld-text-secondary">{stat.label}</p>
                        </div>
                    ))}
                </div>
                <div className="flex flex-col gap-4 p-6 lg:col-span-2">
                    <div className="flex flex-wrap gap-2" role="tablist" aria-label="Sales report ranges">
                        {reportTabs.map((tab) => (
                            <button
                                key={tab}
                                type="button"
                                role="tab"
                                aria-selected={activeRange === tab}
                                aria-controls="provider-sales-report-panel"
                                className={`px-4 py-2 text-sm ${activeRange === tab ? ' underline' : ''}`}
                                onClick={() => onSelectRange(tab)}>
                                {tab}
                            </button>
                        ))}
                    </div>
                    <div
                        id="provider-sales-report-panel"
                        role="region"
                        aria-label="Sales report chart"
                        className="grow p-4">
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={salesChartData} margin={{top: 10, right: 20, left: 0, bottom: 0}}>
                                <defs>
                                    <linearGradient id="providerSalesGradient" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#bdebe0" stopOpacity="0.8" />
                                        <stop offset="100%" stopColor="#effaf5" stopOpacity="0" />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid stroke="#999999" strokeWidth={1} vertical horizontal opacity={0.2} />
                                <XAxis
                                    dataKey="label"
                                    tick={{fill: '#8fa196', fontSize: 12}}
                                    axisLine={false}
                                    tickLine={false}
                                />
                                <YAxis
                                    orientation="right"
                                    domain={[0, maxSalesValue]}
                                    axisLine={false}
                                    tickLine={false}
                                    tick={{fill: '#8fa196', fontSize: 12}}
                                    tickFormatter={(value) => `$${value.toLocaleString()}`}
                                />
                                <Tooltip content={<SalesTooltip />} cursor={{fill: 'rgba(110,196,186,0.1)'}} />
                                <Area
                                    type="linear"
                                    dataKey="value"
                                    stroke="#6ec4ba"
                                    strokeWidth={2}
                                    fill="url(#providerSalesGradient)"
                                    activeDot={{
                                        r: 4,
                                        fill: '#2d8473',
                                        stroke: '#fff',
                                        strokeWidth: 1,
                                    }}
                                />
                            </AreaChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SalesReportChart;
